import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dumbbell, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const formatApiError = (error, fallbackMessage) => {
  const response = error?.response;
  const detail = response?.data?.detail;
  const message = response?.data?.message;

  if (response?.status === 429) {
    return 'Too many attempts. Please wait a moment and try again.';
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg || item?.message)
      .filter(Boolean);
    if (messages.length > 0) {
      return messages.join('; ');
    }
  }

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  if (!response && error?.request) {
    return 'Unable to reach the server. Please check that the service is available and try again.';
  }

  return fallbackMessage;
};

const AuthPage = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ username: '', email: '', password: '' });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetRequestSent, setResetRequestSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        ...loginData,
        email: loginData.email.trim().toLowerCase(),
      });
      onLogin(response.data.user, response.data.access_token);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(formatApiError(error, 'Login failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        ...registerData,
        email: registerData.email.trim().toLowerCase(),
      });
      try { localStorage.setItem('aa_returning', '1'); } catch (e) { /* noop */ }
      onLogin(response.data.user, response.data.access_token);
      toast.success('Account created successfully!');
    } catch (error) {
      toast.error(formatApiError(error, 'Registration failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim().toLowerCase() });
      setResetRequestSent(true);
      toast.success('Password reset request submitted!');
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to submit request'));
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-fade-in">
          <div className="text-center mb-8">
            <div className="inline-block mb-4">
              <Dumbbell className="w-16 h-16 text-blood-500" />
            </div>
            <h1 className="text-4xl font-bold text-blood-400 mb-2 tracking-wider">ANVIL & ASH</h1>
            <p className="text-stone-400 uppercase tracking-widest text-sm">Password Recovery</p>
          </div>

          <Card className="glass p-6 border border-stone-800">
            <button
              onClick={() => {
                setShowForgotPassword(false);
                setResetRequestSent(false);
                setForgotEmail('');
              }}
              className="flex items-center gap-2 text-stone-400 hover:text-blood-400 mb-4 transition-colors"
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </button>

            {resetRequestSent ? (
              <div className="text-center py-6">
                <div className="text-5xl mb-4">&#9993;</div>
                <h2 className="text-xl font-bold text-blood-400 mb-2">Check Your Email</h2>
                <p className="text-stone-400 mb-4">
                  If an account exists with that email, we've sent a password reset link.
                  Check your inbox (and spam folder) and click the link to reset your password.
                </p>
                <p className="text-stone-500 text-xs mb-4">The link expires in 1 hour.</p>
                <Button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetRequestSent(false);
                    setForgotEmail('');
                  }}
                  className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold tracking-wider"
                >
                  Return to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label className="text-blood-400 uppercase tracking-wider">Email Address</Label>
                  <Input
                    type="email"
                    placeholder="your.email@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="bg-stone-900 border-stone-700 text-white mt-2"
                    data-testid="forgot-email-input"
                  />
                  <p className="text-stone-500 text-xs mt-2">
                    Enter the email associated with your account. We'll send you a link to reset your password.
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-6 rounded-xl tracking-wider"
                  disabled={isLoading}
                  data-testid="forgot-submit-btn"
                >
                  {isLoading ? 'SUBMITTING...' : 'REQUEST PASSWORD RESET'}
                </Button>
              </form>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-block mb-4">
            <Dumbbell className="w-16 h-16 text-blood-500" />
          </div>
          <h1 className="text-4xl font-bold text-blood-400 mb-2 tracking-wider" data-testid="auth-title">ANVIL & ASH</h1>
          <p className="text-stone-400 uppercase tracking-widest text-sm">Strength. Discipline. Training that fits.</p>
        </div>

        <Card className="glass p-6 border border-stone-800">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">LOGIN</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">REGISTER</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="login-email" className="text-blood-400 uppercase tracking-wider">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    className="bg-stone-900 border-stone-700 text-white mt-2"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-blood-400 uppercase tracking-wider">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="bg-stone-900 border-stone-700 text-white mt-2"
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-6 rounded-xl btn-primary tracking-wider"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? 'ENTERING...' : 'LOGIN'}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-stone-400 hover:text-blood-400 text-sm transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                <div>
                  <Label htmlFor="register-username" className="text-blood-400 uppercase tracking-wider">Username</Label>
                  <Input
                    id="register-username"
                    type="text"
                    placeholder="johndoe"
                    value={registerData.username}
                    onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                    required
                    className="bg-stone-900 border-stone-700 text-white mt-2"
                    data-testid="register-username-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-blood-400 uppercase tracking-wider">Email</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    className="bg-stone-900 border-stone-700 text-white mt-2"
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-blood-400 uppercase tracking-wider">Password</Label>
                  <Input
                    id="register-password"
                    type="password"
                    placeholder="••••••••"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    required
                    className="bg-stone-900 border-stone-700 text-white mt-2"
                    data-testid="register-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-6 rounded-xl btn-primary tracking-wider"
                  disabled={isLoading}
                  data-testid="register-submit-btn"
                >
                  {isLoading ? 'JOINING...' : 'CREATE ACCOUNT'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </Card>

        <p className="text-center text-stone-500 text-xs mt-4 max-w-sm mx-auto" data-testid="auth-role-hint">
          One login for everyone — coaches &amp; organization staff are taken straight to their <span className="text-amber-400">Coach Dashboard</span>, athletes to their training.
        </p>

        <div className="flex justify-center gap-6 mt-4">
          <a href="/privacy-policy" className="text-stone-500 hover:text-stone-300 text-xs transition-colors" data-testid="auth-privacy-link">
            Privacy Policy
          </a>
          <a href="/terms-of-service" className="text-stone-500 hover:text-stone-300 text-xs transition-colors" data-testid="auth-terms-link">
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;